import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { PageHeader, KPI, StatusPill } from "@/components/Bits";
import { useAuth } from "@/contexts/AuthContext";
import api, { fmtZAR, PROJECT_TYPE_LABELS, ROLE_LABELS } from "@/lib/api";
import { ArrowRight, PlusCircle, ClipboardList, FileText, FolderKanban, Sparkles } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, s] = await Promise.all([
          api.get("/projects").then((r) => r.data).catch(() => []),
          api.get("/surveys").then((r) => r.data).catch(() => []),
        ]);
        setProjects(p);
        setSurveys(s);
        if (user?.role === "admin") {
          api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <PageHeader
          overline={ROLE_LABELS[user?.role] + " · workspace"}
          title={`Welcome, ${user?.name?.split(" ")[0] || "there"}.`}
          description={subtitleFor(user?.role)}
          actions={user?.role === "customer" ? (
            <Link to="/projects/new" className="bq-orange-btn px-5 py-2.5 rounded-md font-semibold flex items-center gap-2" data-testid="dashboard-new-project">
              <PlusCircle size={16} /> New project
            </Link>
          ) : null}
        />

        {/* KPIs by role */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {user?.role === "admin" && stats ? (
            <>
              <KPI label="Total Users" value={stats.total_users} testId="kpi-users" />
              <KPI label="Total Projects" value={stats.total_projects} testId="kpi-projects" />
              <KPI label="Active Projects" value={stats.active_projects} testId="kpi-active" />
              <KPI label="Revenue Collected" value={fmtZAR(stats.revenue_collected)} testId="kpi-revenue" />
            </>
          ) : (
            <>
              <KPI label="My Projects" value={projects.length} testId="kpi-my-projects" />
              <KPI label="Awaiting Quotes" value={projects.filter(p => p.status === "quote_requested").length} testId="kpi-awaiting" />
              <KPI label="Approved" value={projects.filter(p => ["approved","in_progress","completed"].includes(p.status)).length} testId="kpi-approved" />
              <KPI label="My Surveys" value={surveys.length} testId="kpi-surveys" />
            </>
          )}
        </div>

        {/* Quick actions */}
        {user?.role === "customer" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            <ActionCard to="/projects/new" icon={Sparkles} title="Get AI Estimate" desc="60-second rough cost range" testId="action-estimate" />
            <ActionCard to="/projects/new" icon={FileText} title="Request Detailed Quote" desc="Itemised, in 24–48 hours" testId="action-quote" />
            <ActionCard to="/projects/new" icon={ClipboardList} title="Book Site Survey" desc="Pick a slot, we come to you" testId="action-survey" />
          </div>
        )}

        {/* Projects list */}
        <div className="bq-card overflow-hidden">
          <div className="px-6 py-5 border-b border-[var(--bq-border)] flex items-center justify-between">
            <div>
              <div className="overline">Projects</div>
              <h2 className="font-display text-2xl font-bold mt-1">
                {user?.role === "customer" ? "Your projects" : "All projects"}
              </h2>
            </div>
            <Link to="/projects" className="text-sm font-semibold text-[var(--bq-navy)] hover:text-[var(--bq-orange)] flex items-center gap-1" data-testid="see-all-projects">
              See all <ArrowRight size={14} />
            </Link>
          </div>
          {loading ? (
            <div className="p-10 text-center overline">Loading…</div>
          ) : projects.length === 0 ? (
            <div className="p-10 text-center">
              <FolderKanban size={28} className="mx-auto text-[var(--bq-text-muted)]" />
              <div className="overline mt-3">No projects yet</div>
              {user?.role === "customer" && (
                <Link to="/projects/new" className="mt-4 inline-flex bq-orange-btn px-4 py-2 rounded-md text-sm font-semibold" data-testid="empty-new-project">
                  Submit your first project
                </Link>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[var(--bq-bg)] text-[var(--bq-text-muted)]">
                <tr>
                  <th className="text-left px-6 py-3 overline">Project</th>
                  <th className="text-left px-6 py-3 overline hidden md:table-cell">Type</th>
                  <th className="text-left px-6 py-3 overline hidden md:table-cell">Customer</th>
                  <th className="text-left px-6 py-3 overline">Estimate</th>
                  <th className="text-left px-6 py-3 overline">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {projects.slice(0, 8).map((p) => (
                  <tr key={p.id} className="border-t border-[var(--bq-border)] hover:bg-[var(--bq-bg)]" data-testid={`project-row-${p.id}`}>
                    <td className="px-6 py-4">
                      <div className="font-semibold">{p.title}</div>
                      <div className="text-xs text-[var(--bq-text-muted)] mt-0.5">{p.address}</div>
                    </td>
                    <td className="px-6 py-4 hidden md:table-cell">{PROJECT_TYPE_LABELS[p.project_type]}</td>
                    <td className="px-6 py-4 hidden md:table-cell">{p.customer_name}</td>
                    <td className="px-6 py-4">
                      {p.estimate ? `${fmtZAR(p.estimate.min)} – ${fmtZAR(p.estimate.max)}` : "—"}
                    </td>
                    <td className="px-6 py-4"><StatusPill status={p.status} /></td>
                    <td className="px-6 py-4 text-right">
                      <Link to={`/projects/${p.id}`} className="text-[var(--bq-orange)] font-semibold text-sm" data-testid={`open-project-${p.id}`}>
                        Open →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}

function ActionCard({ to, icon: Icon, title, desc, testId }) {
  return (
    <Link to={to} data-testid={testId}
      className="bq-card p-6 hover:-translate-y-1 transition-all flex flex-col gap-3">
      <Icon size={20} className="text-[var(--bq-orange)]" />
      <div className="font-display text-xl font-bold">{title}</div>
      <div className="text-sm text-[var(--bq-text-muted)]">{desc}</div>
    </Link>
  );
}

function subtitleFor(role) {
  switch (role) {
    case "customer": return "Submit, track and approve your construction projects from one place.";
    case "surveyor": return "Manage your survey assignments and submit reports.";
    case "estimator": return "Review submissions and build professional itemised quotes.";
    case "project_manager": return "Drive projects from approval to handover.";
    case "admin": return "Operational overview of all customers, projects and revenue.";
    default: return "Your project workspace.";
  }
}
